import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { debounce } from 'lodash';
import { ClientVendorSearchModel } from 'src/app/classes/client-vendor-search-model';
import { PrimaryTrade } from 'src/app/classes/models';
import { TradeCategory } from 'src/app/classes/models/trade-category';
import { GoogleService } from 'src/app/services/google.service';
import { ToastService } from 'src/app/services/toast.service';
import { TradeService } from 'src/app/services/trade.service';

@Component({
  selector: 'app-client-vendor-search',
  templateUrl: './client-vendor-search.component.html',
  styleUrls: ['./client-vendor-search.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ClientVendorSearchComponent implements OnInit {
  constructor(
    private TradeService: TradeService,
    private GoogleService: GoogleService,
    private ToastService: ToastService
  ) {
    const ClientVendorSearchFilter = localStorage.getItem('ClientVendorSearch');

    // If the search model exists, map any found Trades/TradeCategory ids to the Trade/TradeCategory
    // stored in the search model.
    const SearchModelTradeInfoMap: {
      Trades: { [key: string | number]: PrimaryTrade };
      TradeCategories: { [key: string | number]: TradeCategory };
    } = {
      Trades: {},
      TradeCategories: {}
    };

    // If there are any errors applying the SearchModel from localStorage,
    // wipe the stored SearchModel information.
    if (ClientVendorSearchFilter) {
      try {
        const ClientVendorSearchFilterObject: ClientVendorSearchModel =
          JSON.parse(ClientVendorSearchFilter);

        this.ClientVendorSearchModel = new ClientVendorSearchModel(
          ClientVendorSearchFilterObject
        );

        const {
          Trades,
          TradeCategories,
          address: { name }
        } = this.ClientVendorSearchModel;
        if (name) {
          Object.assign(this.SearchModelObject, { address: name });
        }

        Trades.forEach((Trade) => {
          const { id } = Trade;

          if (!id) {
            throw new Error('Trade is missing id.');
          }

          SearchModelTradeInfoMap['Trades'][id] = Trade;
        });

        TradeCategories.forEach((TradeCategoryRecord) => {
          const { id } = TradeCategoryRecord;

          if (!id) {
            throw new Error('Trade Category is missing id.');
          }

          SearchModelTradeInfoMap['TradeCategories'][id] = TradeCategoryRecord;
        });
      } catch (error) {
        localStorage.removeItem('ClientVendorSearch');
        this.ClientVendorSearchModel = new ClientVendorSearchModel();
      }
    } else {
      this.ClientVendorSearchModel = new ClientVendorSearchModel();
    }

    if (this.showTradeSelection) {
      this.TradeService.getTradeCategories({
        PrimaryTrade: JSON.stringify({ options: { required: true } })
      }).subscribe({
        next: (TradeCategories) => {
          for (let i = 0; i < TradeCategories.length; i += 1) {
            const TradeCategory = TradeCategories[i];

            const { id: TradeCategoryId } = TradeCategory;

            if (!TradeCategoryId) {
              throw new Error('Trade category is missing id.');
            }

            const FoundTradeCategory =
              SearchModelTradeInfoMap['TradeCategories'][TradeCategoryId];

            const TradeCategoryRecord = FoundTradeCategory || TradeCategory;

            const { categoryName, PrimaryTrades } = TradeCategoryRecord;

            if (categoryName && PrimaryTrades) {
              this.TradeCategories.add(TradeCategoryRecord);
              this.FilteredTradeCategories.push(TradeCategoryRecord);

              for (let j = 0; j < PrimaryTrades.length; j += 1) {
                const Trade = PrimaryTrades[j];
                const { id: PrimaryTradeId } = Trade;

                if (!PrimaryTradeId) {
                  throw new Error('Trade is missing id.');
                }

                const FoundTrade =
                  SearchModelTradeInfoMap['Trades'][PrimaryTradeId];
                if (FoundTrade) {
                  Object.assign(FoundTrade, {
                    TradeCategories: [TradeCategoryRecord]
                  });
                  PrimaryTrades.splice(j, 1);
                  j -= 1;
                } else {
                  Object.assign(Trade, {
                    TradeCategories: [TradeCategoryRecord]
                  });
                }
              }

              this.sortTrades(PrimaryTrades);
            }
          }

          this.sortTradeCategories(this.FilteredTradeCategories);
        }
      });
    }
  }

  @ViewChild('TradeCategoryDiv')
  TradeCategoryElement?: ElementRef<HTMLDivElement>;

  @Input() showAdvancedSearch: boolean = true;
  @Input() showTradeSelection: boolean = true;
  @Input() companyTypeSearchText: string = '';

  @Output() advancedSearchEvent = new EventEmitter<ClientVendorSearchModel>();
  @Output() searchEvent = new EventEmitter<ClientVendorSearchModel>();

  TradeCategories: Set<TradeCategory> = new Set([]);
  FilteredTradeCategories: TradeCategory[] = [];

  ClientVendorSearchModel: ClientVendorSearchModel;

  SearchModelObject: {
    trade: string;
    address: string;
  } = {
    trade: '',
    address: ''
  };

  SearchedAddresses: { name: string; id: string }[] = [];

  debouncedAddressSearchFunction?: any;

  // Because we're utilizing references to update trade data during search,
  // we need to store filtered trades, otherwise they will be permanently removed
  // from the selection set.
  StoredTradeMap = new Map<TradeCategory, PrimaryTrade[]>([]);

  sortTrades(Trades: PrimaryTrade[]) {
    Trades.sort((a, b) => {
      if (a.name! < b.name!) {
        return -1;
      }

      return 1;
    });
  }

  sortTradeCategories(TradeCategories: TradeCategory[]) {
    TradeCategories.sort((a, b) => {
      if (a.categoryName! < b.categoryName!) {
        return -1;
      }

      return 1;
    });
  }

  removeModelCircularReferences() {
    // Remove all circular references.
    const { Trades, TradeCategories } = this.ClientVendorSearchModel;

    Trades.forEach((Trade) => delete Trade.TradeCategories);
    TradeCategories.forEach((TradeCategory) => {
      const { PrimaryTrades } = TradeCategory;
      if (PrimaryTrades) {
        PrimaryTrades.forEach((Trade) => delete Trade.TradeCategories);
      }
    });
  }

  advancedSearch() {
    this.removeModelCircularReferences();
    this.advancedSearchEvent.emit(this.ClientVendorSearchModel);
  }

  search() {
    this.removeModelCircularReferences();
    this.searchEvent.emit(this.ClientVendorSearchModel);
  }

  // If an address has been partially typed, but not selected clear the partially typed addresses.
  addressSearchFinished() {
    const {
      address: { id }
    } = this.ClientVendorSearchModel;

    if (!id) {
      Object.assign(this.SearchModelObject, { address: '' });
    }
  }

  addressSearch(event: string) {
    Object.assign(this.SearchModelObject, { address: event });

    const { address } = this.ClientVendorSearchModel;
    Object.assign(address, { id: null, name: null });

    if (event) {
      if (this.debouncedAddressSearchFunction) {
        this.debouncedAddressSearchFunction.cancel();
      }

      this.debouncedAddressSearchFunction = debounce(() => {
        this.GoogleService.addressAutoComplete({ input: event }).subscribe({
          next: (response) => {
            const { suggestions } = response;

            this.SearchedAddresses.splice(0);

            if (Array.isArray(suggestions)) {
              suggestions.forEach((suggestion) => {
                const { placePrediction } = suggestion;

                if (!placePrediction) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Missing address prediction.',
                    { returnError: true }
                  );
                }

                const { text, placeId } = placePrediction;

                if (!text) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Missing address text.',
                    { returnError: true }
                  );
                }

                const { text: name } = text;

                this.SearchedAddresses.push({ name, id: placeId });
              });
            }
          }
        });
      }, 250);

      this.debouncedAddressSearchFunction();
    } else {
      this.SearchedAddresses.splice(0);
    }
  }

  reapplyStoredTrades() {
    this.StoredTradeMap.forEach((Trades, TradeCategory) => {
      const { PrimaryTrades } = TradeCategory;
      if (PrimaryTrades) {
        PrimaryTrades.push(...Trades);
      }
    });

    this.StoredTradeMap.clear();
  }

  filterTradesAndCategories(event: Event) {
    const DivElement = event.target as HTMLDivElement;
    const { textContent: textContentRaw } = DivElement;

    // Replace &nbsp from textContent if found.
    const textContent = textContentRaw?.replace(
      new RegExp(String.fromCharCode(160), 'g'),
      ''
    );

    Object.assign(this.SearchModelObject, { trade: textContent || '' });

    this.reapplyStoredTrades();

    this.FilteredTradeCategories = Array.from(this.TradeCategories).reduce(
      (accumulativeValue, TradeCategory) => {
        const { categoryName, PrimaryTrades } = TradeCategory;

        if (!PrimaryTrades) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Trade category is missing trade information.',
            { returnError: true }
          );
        }

        TradeCategory.PrimaryTrades = PrimaryTrades.reduce(
          (accumulativeValue, Trade) => {
            const { name } = Trade;

            if (
              (name &&
                textContent &&
                name.toLowerCase().includes(textContent.toLowerCase())) ||
              !textContent
            ) {
              accumulativeValue.push(Trade);
            } else {
              const StoredTrades = this.StoredTradeMap.get(TradeCategory) || [];
              StoredTrades.push(Trade);
              this.StoredTradeMap.set(TradeCategory, StoredTrades);
            }

            return accumulativeValue;
          },
          [] as PrimaryTrade[]
        );

        if (
          !textContent ||
          TradeCategory.PrimaryTrades.length > 0 ||
          (categoryName &&
            textContent &&
            categoryName.toLowerCase().includes(textContent.toLowerCase()))
        ) {
          accumulativeValue.push(TradeCategory);
        }

        return accumulativeValue;
      },
      [] as TradeCategory[]
    );

    this.sortTradeCategories(this.FilteredTradeCategories);
  }

  reinsertTradeIntoTradeCategory(Trade: PrimaryTrade) {
    const { TradeCategories } = Trade;

    if (!TradeCategories || TradeCategories.length !== 1) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error deselecting the trade.',
        { returnError: true }
      );
    }

    const TradeCategory = TradeCategories[0];
    const { PrimaryTrades } = TradeCategory;

    if (!PrimaryTrades) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Trade category is missing trades array.',
        { returnError: true }
      );
    }

    PrimaryTrades.push(Trade);
    this.sortTrades(PrimaryTrades);
  }

  selectTradeCategory(TradeCategory: TradeCategory) {
    this.reapplyStoredTrades();
    const { Trades } = this.ClientVendorSearchModel;
    this.ClientVendorSearchModel.Trades = Trades.reduce(
      (accumulativeValue, Trade) => {
        const { TradeCategories } = Trade;

        if (!TradeCategories || TradeCategories.length !== 1) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Trade is missing trade category information.',
            { returnError: true }
          );
        }

        const FoundTradeCategory = TradeCategories[0];
        if (FoundTradeCategory !== TradeCategory) {
          accumulativeValue.push(Trade);
        } else {
          this.reinsertTradeIntoTradeCategory(Trade);
        }

        return accumulativeValue;
      },
      [] as PrimaryTrade[]
    );

    this.TradeCategories.delete(TradeCategory);
    const { TradeCategories } = this.ClientVendorSearchModel;
    TradeCategories.push(TradeCategory);
    Object.assign(this.SearchModelObject, { trade: '' });
    (this.TradeCategoryElement?.nativeElement as HTMLDivElement).textContent =
      '';
    this.FilteredTradeCategories = Array.from(this.TradeCategories);
    this.sortTradeCategories(this.FilteredTradeCategories);
  }

  deselectTradeCategory(TradeCategory: TradeCategory, index: number) {
    this.ClientVendorSearchModel['TradeCategories'].splice(index, 1);
    this.TradeCategories.add(TradeCategory);
    this.FilteredTradeCategories = Array.from(this.TradeCategories);
    this.sortTradeCategories(this.FilteredTradeCategories);
  }

  selectTrade(Trade: PrimaryTrade, TradeCategory: TradeCategory) {
    this.reapplyStoredTrades();
    const { PrimaryTrades } = TradeCategory;

    if (PrimaryTrades) {
      const TradeIndex = PrimaryTrades.findIndex(
        (FoundTrade) => FoundTrade === Trade
      );
      PrimaryTrades.splice(TradeIndex, 1);
    }

    const { Trades } = this.ClientVendorSearchModel;
    Trades.push(Trade);
    Object.assign(this.SearchModelObject, { trade: '' });
    (this.TradeCategoryElement?.nativeElement as HTMLDivElement).textContent =
      '';
    this.FilteredTradeCategories = Array.from(this.TradeCategories);
    this.sortTradeCategories(this.FilteredTradeCategories);
  }

  // Need to reattach Trade to TradeCategory
  deselectTrade(Trade: PrimaryTrade, index: number) {
    this.ClientVendorSearchModel['Trades'].splice(index, 1);
    this.reinsertTradeIntoTradeCategory(Trade);
  }

  selectAddress({ name, id }: { name: string; id: string }) {
    Object.assign(this.SearchModelObject, { address: name });
    const { address } = this.ClientVendorSearchModel;
    Object.assign(address, { name, id });
  }

  ngOnInit(): void {
    if (!this.companyTypeSearchText) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing company type search text.',
        { returnError: true }
      );
    }
  }
}
